// pipe: ROMS+Monday P4.7 — the on-shift position-sharing loop (opt-in only; owner-approved full
// tracking, strictest privacy build).
//
// Module-level on purpose (the `erp-site-scope.ts` precedent): ONE loop per app session, observable
// from any screen via LiveData so the visible "sharing is ON" indicator always reflects the real
// state. The loop only ever reports the CURRENT user's own position through the consent-gated
// `POST /api/me/position` core — the server re-checks the admin-set `opt_in_position_tracking`
// employee flag on EVERY ping (a 403 stops the loop and surfaces the "not enabled" notice), derives
// `employee_ref` itself, and stamps its own clock. Nothing is captured or buffered client-side
// beyond the in-flight reading.
//
// Capture uses the WEB `navigator.geolocation` API, which also works inside the Capacitor iOS /
// Android webview (the OS additionally shows its own native location-in-use indicator there —
// defense in depth on visibility). Wiring the `@capacitor/geolocation` plugin (background/native
// permission UX) needs a dependency install and is a documented follow-up — see
// docs/staff-position-tracking-compliance.md; the web path here works regardless.
import { LiveData } from '@toeverything/infra';

import type { ErpService } from './services/erp';

export interface PositionTrackingState {
  /** True while the sharing loop is running (drives the visible indicator). */
  active: boolean;
  /** Server timestamp of the last accepted ping (null before the first). */
  lastPingAt: string | null;
  /** The last capture/ingest failure, or 'forbidden' when the server refused (not enabled). */
  lastError: string | null;
}

export const positionTracking$ = new LiveData<PositionTrackingState>({
  active: false,
  lastPingAt: null,
  lastError: null,
});

/** Default ping cadence — every 5 minutes while sharing is on. */
export const POSITION_PING_INTERVAL_MS = 5 * 60_000;

let timer: ReturnType<typeof setInterval> | null = null;

function readPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('geolocation unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, err => {
      reject(new Error(err.message || 'geolocation denied'));
    });
  });
}

async function pingOnce(erp: ErpService): Promise<void> {
  try {
    const pos = await readPosition();
    const receipt = await erp.sendMyPosition(
      pos.coords.latitude,
      pos.coords.longitude,
      pos.coords.accuracy ?? undefined
    );
    positionTracking$.next({
      ...positionTracking$.value,
      lastPingAt: receipt.ts,
      lastError: null,
    });
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status === 403) {
      // The server refused (tracking not enabled for this employee, or consent withdrawn) —
      // fail closed: stop the loop; the screen renders the "not enabled" notice.
      stopPositionTracking('forbidden');
      return;
    }
    positionTracking$.next({
      ...positionTracking$.value,
      lastError: e instanceof Error ? e.message : 'error',
    });
  }
}

/** Start the sharing loop (immediate first ping + the interval). Idempotent. */
export function startPositionTracking(
  erp: ErpService,
  intervalMs = POSITION_PING_INTERVAL_MS
) {
  if (timer !== null) {
    return;
  }
  positionTracking$.next({ active: true, lastPingAt: null, lastError: null });
  void pingOnce(erp);
  timer = setInterval(() => void pingOnce(erp), intervalMs);
}

/** Stop the sharing loop (the visible indicator turns off). Idempotent. */
export function stopPositionTracking(reason: string | null = null) {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  positionTracking$.next({
    ...positionTracking$.value,
    active: false,
    lastError: reason,
  });
}
